import { dfsGridToLatLon } from './kma-grid';

export type RuntimeMode = 'unconfigured' | 'local' | 'firebase-emulator' | 'firebase';
/**
 * Where the active site is, for weather lookups only. There is no site master table yet, so the
 * coordinates are runtime configuration rather than a literal in a screen. Either an explicit
 * decimal pair or a 기상청 DFS grid pair may be supplied; the grid form is what the legacy
 * factory configuration recorded, so it is accepted and converted here.
 */
export type SiteWeatherLocation = {
  latitude: number;
  longitude: number;
  coordinateSource: 'lat-lon' | 'kma-grid';
};
export type RuntimeConfig = {
  mode: RuntimeMode;
  telemetryEnabled: boolean;
  release: string;
  siteWeather: SiteWeatherLocation | null;
  firebase: null | {
    projectId: string;
    apiKey: string;
    appId: string;
    authDomain: string;
    appCheckSiteKey: string;
  };
};

const numeric = (value: string | boolean | undefined) => {
  const text = String(value ?? '').trim();
  if (text.length === 0) return null;
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Fail-closed: an absent, partial or out-of-range location yields `null` so the weather panes stay
 * visibly empty. It never throws, because an unknown site location must not stop the app booting.
 */
export function readSiteWeatherLocation(
  env: Record<string, string | boolean | undefined>,
): SiteWeatherLocation | null {
  const latitude = numeric(env.VITE_SITE_WEATHER_LATITUDE);
  const longitude = numeric(env.VITE_SITE_WEATHER_LONGITUDE);
  if (
    latitude !== null &&
    longitude !== null &&
    Math.abs(latitude) <= 90 &&
    Math.abs(longitude) <= 180
  )
    return { latitude, longitude, coordinateSource: 'lat-lon' };
  const nx = numeric(env.VITE_SITE_WEATHER_GRID_NX);
  const ny = numeric(env.VITE_SITE_WEATHER_GRID_NY);
  if (nx === null || ny === null || !Number.isInteger(nx) || !Number.isInteger(ny)) return null;
  if (nx < 1 || nx > 999 || ny < 1 || ny > 999) return null;
  const converted = dfsGridToLatLon(nx, ny);
  if (!converted || Math.abs(converted.latitude) > 90 || Math.abs(converted.longitude) > 180)
    return null;
  return { ...converted, coordinateSource: 'kma-grid' };
}

export function readRuntimeConfig(
  env: Record<string, string | boolean | undefined>,
): RuntimeConfig {
  const selected = String(env.VITE_AUTH_ADAPTER ?? 'unconfigured');
  if (!['unconfigured', 'local', 'firebase-emulator', 'firebase'].includes(selected))
    throw new Error(`Unsupported runtime adapter: ${selected}`);
  const mode = selected as RuntimeMode;
  const telemetryEnabled = String(env.VITE_TELEMETRY_ENABLED ?? 'false') === 'true';
  const release = String(env.VITE_APP_RELEASE ?? 'development');
  const siteWeather = readSiteWeatherLocation(env);
  if (mode !== 'firebase') return { mode, telemetryEnabled, release, siteWeather, firebase: null };
  const firebase = {
    projectId: String(env.VITE_FIREBASE_PROJECT_ID ?? ''),
    apiKey: String(env.VITE_FIREBASE_API_KEY ?? ''),
    appId: String(env.VITE_FIREBASE_APP_ID ?? ''),
    authDomain: String(env.VITE_FIREBASE_AUTH_DOMAIN ?? ''),
    appCheckSiteKey: String(env.VITE_FIREBASE_APPCHECK_SITE_KEY ?? ''),
  };
  if (Object.values(firebase).some((value) => value.trim().length === 0))
    throw new Error('Firebase runtime configuration is incomplete');
  if (firebase.projectId.startsWith('demo-'))
    throw new Error('Production runtime does not accept demo Firebase projects');
  return { mode, telemetryEnabled, release, siteWeather, firebase };
}

export const runtimeConfig = readRuntimeConfig(import.meta.env);

/** A configured coordinate must belong to the explicitly selected site. */
export function readSelectedSiteWeatherLocation(
  selectedSiteId: string | null,
  env: Record<string, string | boolean | undefined> = import.meta.env,
): SiteWeatherLocation | null {
  const configuredSiteId = String(env.VITE_SITE_WEATHER_SITE_ID ?? '').trim();
  if (!selectedSiteId || !configuredSiteId || selectedSiteId !== configuredSiteId) return null;
  return readSiteWeatherLocation(env);
}
